"use client";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCheckout } from "@/hooks/billing/useCheckout";
import { usePaymentSelectionStore } from "@/stores/paymentSelection/store";
import type { BillingCycle, PlanType } from "@/stores/paymentSelection/types";
import { PLANS, PLAN_RESOURCE_SUMMARY } from "./constants";

interface TrialSignupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TrialSignupDialog({
  open,
  onOpenChange,
}: TrialSignupDialogProps) {
  const { handleCheckout } = useCheckout();
  const selectedBillingCycle = usePaymentSelectionStore(
    (s) => s.selectedBillingCycle,
  );
  const setSelectedPlan = usePaymentSelectionStore((s) => s.setSelectedPlan);
  const setSelectedBillingCycle = usePaymentSelectionStore(
    (s) => s.setSelectedBillingCycle,
  );

  const cycle = selectedBillingCycle ?? "annual";
  const recommended = PLANS.find((p) => p.type === "pro") ?? PLANS[0];
  const alternative = PLANS.find((p) => p.type !== recommended.type);

  const recommendedMonthly =
    cycle === "annual"
      ? Math.round(recommended.yearlyPrice / 12)
      : recommended.monthlyPrice;
  const recommendedAnnualSavings =
    recommended.monthlyPrice * 12 - recommended.yearlyPrice;
  const alternativeMonthly = alternative
    ? cycle === "annual"
      ? Math.round(alternative.yearlyPrice / 12)
      : alternative.monthlyPrice
    : 0;

  const startTrial = async (planType: string) => {
    const plan = planType as PlanType;
    setSelectedPlan(plan);
    setSelectedBillingCycle(cycle);
    await handleCheckout(plan, cycle);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] gap-0 p-0">
        <DialogHeader className="space-y-2 border-b border-border p-6">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Free trial
          </span>
          <DialogTitle className="text-lg font-medium">
            Start your 14-day free trial
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Unlock optimization tools, content generation, and gap analysis.
          </p>
        </DialogHeader>

        <div className="space-y-5 p-6">
          <Tabs
            value={cycle}
            onValueChange={(v) => setSelectedBillingCycle(v as BillingCycle)}
          >
            <TabsList>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
              <TabsTrigger value="annual">Annual</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="space-y-4 rounded-xl border-[0.5px] border-border bg-card p-5">
            <div className="space-y-3">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Recommended
              </span>
              <div className="flex items-baseline justify-between gap-6">
                <div className="text-base font-medium">{recommended.name}</div>
                <div>
                  <span className="text-3xl font-medium tabular-nums text-foreground">
                    ${recommendedMonthly}
                  </span>
                  <span className="text-sm text-muted-foreground"> /mo</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {recommended.description} ·{" "}
                {cycle === "annual" ? "billed annually" : "billed monthly"} ·
                cancel anytime
              </p>
            </div>

            <div className="border-t border-border" />

            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {PLAN_RESOURCE_SUMMARY[
                recommended.type as keyof typeof PLAN_RESOURCE_SUMMARY
              ]?.map((row) => (
                <div key={row.key} className="space-y-0.5">
                  <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {row.label}
                  </dt>
                  <dd className="text-foreground tabular-nums">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="space-y-2">
            <Button
              onClick={() => startTrial(recommended.type)}
              className="w-full"
            >
              Start 14-day free trial
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              No charge until day 15. Cancel anytime.
            </p>
          </div>

          {cycle === "monthly" && (
            <div className="flex items-center justify-between gap-3 rounded-lg bg-muted/60 px-4 py-3">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">
                  Save ${recommendedAnnualSavings.toLocaleString()}/year
                </span>{" "}
                with annual billing
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedBillingCycle("annual")}
              >
                Switch to annual
              </Button>
            </div>
          )}

          {alternative && (
            <div className="text-center text-sm">
              <span className="text-muted-foreground">Need less? </span>
              <button
                type="button"
                onClick={() => startTrial(alternative.type)}
                className="text-foreground underline underline-offset-2 hover:no-underline"
              >
                Start with {alternative.name} — ${alternativeMonthly}/mo
              </button>
            </div>
          )}

          <div className="text-center text-xs text-muted-foreground">
            Need more?{" "}
            <a
              href="https://ripenn.ai/book"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground underline underline-offset-2 hover:no-underline"
            >
              Book a demo to explore Enterprise
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
